/**
 * components/qrCode.tsx
 * Renders a value as a monochrome QR code with NO native module and NO canvas.
 * react-native-svg is stubbed out on web (webpack: 'react-native-svg': false),
 * so we build a plain SVG string from the qrcode-generator module matrix and
 * show it as a data-URI <Image>. Works on RN-Web; black-on-white (SK monochrome).
 *
 * Used by the "Guest QR" card so a venue can display/print the code guests scan
 * to self-join.
 */

import React, { useMemo } from 'react';
import { Image, View } from 'react-native';
import qrcode from 'qrcode-generator';

interface QrCodeProps {
  /** The string to encode (here: the guest self-join URL). */
  value: string;
  /** Rendered pixel size of the square. Default 200. */
  size?: number;
  /** Quiet-zone modules around the code (spec recommends 4). Default 4. */
  quietZone?: number;
}

/** Build a self-contained SVG document (string) for the QR matrix. */
function buildSvg(value: string, quietZone: number): { svg: string; total: number } {
  const qr = qrcode(0, 'M'); // type 0 = auto-fit version, 'M' = ~15% ECC
  qr.addData(value);
  qr.make();
  const n = qr.getModuleCount();
  const total = n + quietZone * 2;
  // One <rect> per dark module, in a viewBox sized to the module grid so the
  // <Image> can scale it to any pixel size crisply.
  let rects = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) {
        rects += `<rect x="${c + quietZone}" y="${r + quietZone}" width="1" height="1"/>`;
      }
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges">` +
    `<rect width="${total}" height="${total}" fill="#fff"/>` +
    `<g fill="#000">${rects}</g>` +
    `</svg>`;
  return { svg, total };
}

export default function QrCode({ value, size = 200, quietZone = 4 }: QrCodeProps) {
  const uri = useMemo(() => {
    const { svg } = buildSvg(value, quietZone);
    // base64 data-URI — RN-Web's <Image> reliably renders this (it dropped the
    // utf8-encoded form). btoa needs Latin-1; the SVG is ASCII so it's safe.
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }, [value, quietZone]);

  return (
    <View style={{ width: size, height: size, backgroundColor: '#fff' }}>
      <Image source={{ uri }} style={{ width: size, height: size }} resizeMode="contain" />
    </View>
  );
}
